import { PLATFORM_LABEL, resolvePlatform, type Platform } from '@discovr/types';

/** How the wizard names a creator's identity column. The server sends the
 *  full dropdown label; these are the short forms the grid header, banner and
 *  warnings need, which differ per platform (Instagram username vs YouTube
 *  channel). */
export interface ImportIdentityWording {
  columnLabel: string;
  missingSingular: string;
  missingPlural: string;
  emptyCellPrompt: string;
}

const EMPTY_CELL_PROMPT = 'Add handle';

const USERNAME_WORDING: ImportIdentityWording = {
  columnLabel: 'Username',
  missingSingular: 'missing username',
  missingPlural: 'missing usernames',
  emptyCellPrompt: EMPTY_CELL_PROMPT,
};

const CHANNEL_WORDING: ImportIdentityWording = {
  columnLabel: 'Channel',
  missingSingular: 'missing channel',
  missingPlural: 'missing channels',
  emptyCellPrompt: EMPTY_CELL_PROMPT,
};

const WORDING_BY_PLATFORM: Record<Platform, ImportIdentityWording> = {
  instagram: USERNAME_WORDING,
  youtube: CHANNEL_WORDING,
};

const INDEFINITE_ARTICLE_BY_PLATFORM: Record<Platform, string> = {
  instagram: 'an',
  youtube: 'a',
};

/** An unrecognised platform falls back to the username wording, which is
 *  also what the server's field label says in that case. */
export function importIdentityWording(campaignPlatform: string | null | undefined): ImportIdentityWording {
  const platform = resolvePlatform(campaignPlatform);
  return platform ? WORDING_BY_PLATFORM[platform] : USERNAME_WORDING;
}

/** "Urbanic is an Instagram campaign, so you'll only see Instagram fields."
 *  Null when the platform isn't recognised: there is nothing true to say. */
export function importPlatformSubtitle(
  campaignName: string | null | undefined,
  campaignPlatform: string | null | undefined,
): string | null {
  const platform = resolvePlatform(campaignPlatform);
  if (!platform) return null;
  const name = PLATFORM_LABEL[platform];
  const subject = (campaignName ?? '').trim() || 'This';
  return `${subject} is ${INDEFINITE_ARTICLE_BY_PLATFORM[platform]} ${name} campaign, so you'll only see ${name} fields.`;
}
